import type {
  AutoReLease,
  ContractSearchCondition,
  ContractStatus,
  IsThisInclude,
  OnBalance,
  PaymentCycle,
  ReLeaseAndContractExpiredType,
  ReLeaseFeesCalcMethod,
  TreatmentAfterExpiration,
  TreatmentFixedAssetControl,
} from '@libs/enums';
import type {
  DateRangeObject,
  ItemNames,
  NumericRangeObject,
  OrEmpty,
  WithBlankSearchObject,
  WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  DateRange,
  ItemName,
  Locale,
  Numeric,
  NumericRange,
  SingleDate,
  SingleValue,
  ValueSet,
  WithBlankSearch,
  WithExclusion,
} from '@ps-smart-cp/ppfw-client';

/**
 * リース契約情報
 */
interface Schema {
  /**
   * 締結時稟議決裁番号
   */
  conclusionApprovalNo: string;
  /**
   * 締結時摘要
   */
  conclusionSummary: string;
  /**
   * 契約年月日
   */
  contractDate: Date | null;
  /**
   * 契約名称
   */
  name: string;
  /**
   * 契約名称
   */
  contractName: string;
  /**
   * 契約番号
   */
  contractNo: string;
  /**
   * 契約予備名称1
   */
  contractSpareName01: string;
  /**
   * 契約予備名称2
   */
  contractSpareName02: string;
  /**
   * 見積現金購入額
   */
  estimateCashPurchaseAmount: string;
  /**
   * リース契約ID
   */
  laContractId: number | null;
  /**
   * リース満了年月日
   */
  laExpiredDate: Date | null;
  /**
   * リース開始年月日
   */
  laStartDate: Date | null;
  /**
   * リース期間
   */
  leasePeriodMonths: string;
  /**
   * 再リース回数
   */
  reLeaseCount: string;
  /**
   * 満了後の扱い
   */
  treatmentAfterExpiration: OrEmpty<TreatmentAfterExpiration>;
  /**
   * 固定資産管理上での扱い
   */
  treatmentFixedAssetControl: TreatmentFixedAssetControl;
  /**
   * 再リース及び満了
   */
  reLeaseAndExpiration: OrEmpty<ReLeaseAndContractExpiredType>;
  /**
   * 解約年月日
   */
  terminationDate: Date | null;
  /**
   * 解約損害金
   */
  terminationDamage: string;
  /**
   * 契約年月日(テキスト)
   */
  rawContractDate: string;
  /**
   * リース開始年月日(テキスト)
   */
  rawLaStartDate: string;
  /**
   * リース期間
   */
  leasePeriod: string;
  /**
   * リース満了年月日(テキスト)
   */
  rawLaExpiredDate: string;
  /**
   * 契約名称コード
   */
  contractNameCode: string;
  /**
   * 契約番号範囲(抽出条件)
   */
  contractNoRange: WithExclusionObject;
  /**
   * 解約年月日
   */
  cancelDate: Date | null;
  /**
   * 解約年月日(テキスト)
   */
  rawCancelDate: string;
  /**
   * 解約損害金
   */
  cancelDamage: string;
  /**
   * 変更後契約番号
   */
  contractNoAfterChange: string;
  /**
   * 再リース
   */
  reLease: IsThisInclude;
  /**
   * 抽出条件に合致する全データに対して、取消処理を実行する
   */
  doCancelByCondition: boolean;
  /**
   * 契約状態区分(抽出条件)
   */
  contractConditionClassCondition: Set<ContractStatus>;
  /**
   * 契約状態区分
   */
  // TODO: FFW-96 <contractConditionClassCondition⇒contractStatus>
  contractConditionClass: ContractStatus;
  /**
   * リース開始年月日(抽出条件)
   */
  laStartDateCondition: DateRangeObject;
  /**
   * リース満了年月日(抽出条件)
   */
  laExpiredDateCondition: DateRangeObject;
  /**
   * 解約年月日(抽出条件)
   */
  cancelDateCondition: DateRangeObject;
  /**
   * 合併受入
   */
  acquiredMerger: boolean;
  /**
   * 合併受入年月日
   */
  acquiredMergerDate: Date | null;
  /**
   * 合併受入年月日(テキスト)
   */
  rawAcquiredMergerDate: string;
  /**
   * 自動再リースする
   */
  isAutomaticReLease: boolean;
  /**
   * 再リース料計算区分
   */
  reLeaseFeeCalculationClass: ReLeaseFeesCalcMethod;
  /**
   * 再リース料計算区分(テキスト)
   */
  rawReLeaseFeeCalculationClass: string;
  /**
   * 再リース料(手入力)
   */
  manualInputReLeaseFee: string;
  /**
   * 再リース支払サイクル
   */
  reLeasePaymentCycle: PaymentCycle;
  /**
   * 再リース支払サイクル(テキスト)
   */
  rawReLeasePaymentCycle: string;
  /**
   * 再リース支払日
   */
  reLeasePaymentDay: string;
  /**
   * 契約年月日(抽出条件)
   */
  contractDateCondition: DateRangeObject;
  /**
   * 契約番号(抽出条件)
   */
  contractNoCondition: string;
  /**
   * 契約番号単位で集計する
   */
  hasTotalByContractNoUnits: boolean;
  /**
   * オンバランス
   */
  onBalance: IsThisInclude;
  /**
   * リース開始予定年月日
   */
  laStartScheduledDate: Date | null;
  /**
   * 契約予備名称1(抽出条件)
   */
  contractSpareNameOneCondition: WithBlankSearchObject;
  /**
   * 契約予備名称2(抽出条件)
   */
  contractSpareNameTwoCondition: WithBlankSearchObject;
  /**
   * 見積現金購入額(抽出条件)
   */
  estimateCashPurchaseAmountCondition: NumericRangeObject;
  /**
   * 締結時稟議決裁番号(抽出条件)
   */
  conclusionApprovalNoCondition: WithBlankSearchObject;
  /**
   * リース開始予定年月日(抽出条件)
   */
  laStartScheduledDateCondition: DateRangeObject;
  /**
   * 更新中契約(抽出条件)
   */
  updatingContractsCondition: IsThisInclude;
  /**
   * リース期間(抽出条件)
   */
  leasePeriodCondition: NumericRangeObject;
  /**
   * 合併受入(抽出条件)
   */
  acquiredMergerCondition: IsThisInclude;
  /**
   * 合併受入年月日(抽出条件)
   */
  acquiredMergerDateCondition: DateRangeObject;
  /**
   * 移行年月日
   */
  migrationDate: Date | null;
  /**
   * 満了年月日
   */
  expiredDate: Date | null;
  /**
   * 契約検索条件
   */
  contractSearchCondition: ContractSearchCondition;
  /**
   * 自動再リース(Grid表示用)
   */
  automaticReLeaseGrid: AutoReLease;
  /**
   * 元本残高
   */
  principalBalance: string;
  /**
   * オンバランス(Grid表示用)
   */
  onBalanceGrid: OrEmpty<OnBalance>;
  /**
   * 満了後の扱い(抽出条件)
   */
  treatmentAfterExpirationCondition: Set<TreatmentAfterExpiration>;
  /**
   * 次回の契約番号
   */
  nextContractNo: string;
  /**
   * 満了後の扱い(テキスト)
   */
  rawTreatmentAfterExpiration: string;
  /**
   * 再リース及び満了(テキスト)
   */
  rawReLeaseAndExpiration: string;
}

interface Record {
  conclusionApprovalNo: string;
  conclusionSummary: string;
  contractDate: string;
  name: string;
  contractNo: string;
  spareName01: string;
  spareName02: string;
  estimateCashPurchaseAmount: string;
  laContractId: number | null;
  laExpiredDate: string;
  laStartDate: string;
  leasePeriodMonths: string;
  reLeaseCount: string;
  treatmentAfterExpiration: string;
  treatmentFixedAssetControl: string;
  reLeaseAndExpiration: string;
  terminationDate: string;
  terminationDamage: string;
  leasePeriod: string;
  contractNameCode: string;
  contractName: string;
  contractSpareNameOne: string;
  contractSpareNameTwo: string;
  contractNoRange: string;
  isContractNoRangeExcluded: boolean;
  cancelDate: string;
  cancelDamage: string;
  contractNoAfterChange: string;
  reLease: string;
  laCompany: string;
  laStartDateFrom: string;
  laStartDateTo: string;
  laExpiredDateFrom: string;
  laExpiredDateTo: string;
  cancelDateFrom: string;
  cancelDateTo: string;
  doCancelByCondition: boolean;
  contractConditionClassCondition: string[];
  contractConditionClass: string;
  acquiredMerger: boolean;
  acquiredMergerDate: string;
  laNameMstCode: string;
  isAutomaticReLease: boolean;
  reLeaseFeeCalculationClass: string;
  manualInputReLeaseFee: string;
  reLeasePaymentCycle: string;
  reLeasePaymentDay: string;
  contractDateFrom: string;
  contractDateTo: string;
  contractNoCondition: string;
  hasTotalByContractNoUnits: boolean;
  onBalance: string;
  contractExpiredCount: string;
  isContractSpareNameOneBlankOnly: boolean;
  isContractSpareNameTwoBlankOnly: boolean;
  estimateCashPurchaseAmountFrom: string;
  estimateCashPurchaseAmountTo: string;
  isConclusionApprovalNoBlankOnly: boolean;
  laStartScheduledDateFrom: string;
  laStartScheduledDateTo: string;
  laStartScheduledDate: string;
  updatingContracts: string;
  leasePeriodConditionFrom: string;
  leasePeriodConditionTo: string;
  paymentExpiration: string;
  paymentMethodCheckList: string[];
  acquiredMergerCondition: string;
  acquiredMergerDateFrom: string;
  acquiredMergerDateTo: string;
  migrationDate: string;
  expiredDate: string;
  contractSearchCondition: string;
  automaticReLeaseGrid: string;
  principalBalance: string;
  treatmentAfterExpirationCondition: string[];
  nextContractNo: string;
  approvalNoOnConclusion: string;
  summaryOnConclusion: string;
}

const attributes = {
  conclusionApprovalNo: Characters.preset.freeFormat({ maxLen: 20 }),
  conclusionSummary: Characters.preset.freeFormat({ maxLen: 50 }),
  contractDate: SingleDate.ymd(),
  name: Characters.preset.freeFormat({ maxLen: 100 }),
  contractName: Characters.preset.freeFormat({ maxLen: 100 }),
  contractNo: Characters.preset.halfWidth({ maxLen: 20, required: true }),
  contractSpareName01: Characters.preset.freeFormat({ maxLen: 100 }),
  contractSpareName02: Characters.preset.freeFormat({ maxLen: 100 }),
  estimateCashPurchaseAmount: Numeric.preset.currency(),
  laExpiredDate: SingleDate.ymd(),
  laStartDate: SingleDate.ymd(),
  leasePeriodMonths: Numeric.of(1, 999, 0),
  reLeaseCount: Numeric.of(0, 999, 0),
  treatmentAfterExpiration: SingleValue.shortValue(),
  treatmentFixedAssetControl: SingleValue.shortValue(),
  reLeaseAndExpiration: SingleValue.longValue(),
  terminationDate: SingleDate.ymd(true),
  terminationDamage: Numeric.preset.currency(undefined, true),
  leasePeriod: Numeric.of(1, 999, 0),
  contractNameCode: Characters.preset.halfWidth({ maxLen: 20 }),
  contractNoRange: WithExclusion.of(Characters.preset.halfWidth({ maxLen: 250 })),
  cancelDate: SingleDate.ymd(true),
  cancelDamage: Numeric.preset.currency(undefined, true),
  contractNoAfterChange: Characters.preset.halfWidth({ maxLen: 20 }),
  reLease: SingleValue.shortValue(),
  doCancelByCondition: Bool.new(),
  contractConditionClassCondition: ValueSet.new(),
  laStartDateCondition: DateRange.ymd(),
  laExpiredDateCondition: DateRange.ymd(),
  cancelDateCondition: DateRange.ymd(),
  acquiredMerger: Bool.new(),
  acquiredMergerDate: SingleDate.ymd(),
  isAutomaticReLease: Bool.new(),
  reLeaseFeeCalculationClass: SingleValue.shortValue(),
  manualInputReLeaseFee: Numeric.preset.currency(),
  reLeasePaymentCycle: SingleValue.shortValue(),
  reLeasePaymentDay: Numeric.of(1, 31, 0),
  contractDateCondition: DateRange.ymd(),
  // TODO: FFW-113 EFW12022(半角文字のみの入力制御)の対応
  contractNoCondition: Characters.preset.freeFormat({ minLen: 0, maxLen: 99999, allowCrlf: true }),
  hasTotalByContractNoUnits: Bool.new(),
  onBalance: SingleValue.shortValue(),
  laStartScheduledDate: SingleDate.ymd(),
  contractSpareNameOneCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  contractSpareNameTwoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  estimateCashPurchaseAmountCondition: NumericRange.of(0, 9_999_999_999_999, 0),
  conclusionApprovalNoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 20 })),
  laStartScheduledDateCondition: DateRange.ymd(),
  updatingContractsCondition: SingleValue.shortValue(),
  leasePeriodCondition: NumericRange.of(0, 999, 0),
  acquiredMergerCondition: SingleValue.shortValue(),
  acquiredMergerDateCondition: DateRange.ymd(),
  migrationDate: SingleDate.ymd(),
  expiredDate: SingleDate.ymd(),
  contractSearchCondition: SingleValue.longValue(),
  automaticReLeaseGrid: SingleValue.longValue(),
  principalBalance: Numeric.preset.currency(),
  onBalanceGrid: SingleValue.longValue(),
  treatmentAfterExpirationCondition: ValueSet.new(),
  nextContractNo: Characters.preset.halfWidth({ maxLen: 20, required: true }),
};

const names = {
  conclusionApprovalNo: ItemName.of([Locale.ja, '締結時稟議決裁番号']),
  conclusionSummary: ItemName.of([Locale.ja, '締結時摘要']),
  contractDate: ItemName.of([Locale.ja, '契約年月日']),
  name: ItemName.of([Locale.ja, '契約名称']),
  contractName: ItemName.of([Locale.ja, '契約名称']),
  contractNo: ItemName.of([Locale.ja, '契約番号']),
  contractSpareName01: ItemName.of([Locale.ja, '契約予備名称1']),
  contractSpareName02: ItemName.of([Locale.ja, '契約予備名称2']),
  estimateCashPurchaseAmount: ItemName.of([Locale.ja, '見積現金購入額']),
  laExpiredDate: ItemName.of([Locale.ja, 'リース満了年月日']),
  laStartDate: ItemName.of([Locale.ja, 'リース開始年月日']),
  leasePeriodMonths: ItemName.of([Locale.ja, 'リース期間']),
  reLeaseCount: ItemName.of([Locale.ja, '再リース回数']),
  treatmentAfterExpiration: ItemName.of([Locale.ja, '満了後の扱い']),
  treatmentFixedAssetControl: ItemName.of([Locale.ja, '固定資産管理上での扱い']),
  reLeaseAndExpiration: ItemName.of([Locale.ja, '再リース及び満了']),
  terminationDate: ItemName.of([Locale.ja, '解約年月日']),
  terminationDamage: ItemName.of([Locale.ja, '解約損害金']),
  leasePeriod: ItemName.of([Locale.ja, 'リース期間'], [Locale.en, 'Lease Period']),
  contractNameCode: ItemName.of([Locale.ja, '契約名称コード'], [Locale.en, 'Contract Name Code']),
  contractNoRange: ItemName.of([Locale.ja, '契約番号範囲'], [Locale.en, 'Contract No Range']),
  cancelDate: ItemName.of([Locale.ja, '解約年月日'], [Locale.en, 'Cancel Date']),
  cancelDamage: ItemName.of([Locale.ja, '解約損害金'], [Locale.en, 'Cancel Damage']),
  contractNoCondition: ItemName.of([Locale.ja, '契約番号'], [Locale.en, 'Contract No Condition']),
  contractNoAfterChange: ItemName.of([Locale.ja, '変更後契約番号'], [Locale.en, 'Contract No After Change']),
  reLease: ItemName.of([Locale.ja, '再リース'], [Locale.en, 'Re Lease']),
  doCancelByCondition: ItemName.of(
    [Locale.ja, '抽出条件に合致する全データに対して、取消処理を実行する'],
    [Locale.en, 'Do Cancel By Condition'],
  ),
  contractConditionClassCondition: ItemName.of([Locale.ja, '契約状態区分']),
  contractConditionClass: ItemName.of([Locale.ja, '契約状態区分']),
  laStartDateCondition: ItemName.of([Locale.ja, 'リース開始年月日']),
  laExpiredDateCondition: ItemName.of([Locale.ja, 'リース満了年月日']),
  cancelDateCondition: ItemName.of([Locale.ja, '解約年月日']),
  acquiredMerger: ItemName.of([Locale.ja, '合併受入']),
  acquiredMergerDate: ItemName.of([Locale.ja, '合併受入年月日']),
  isAutomaticReLease: ItemName.of([Locale.ja, '自動再リースする']),
  reLeaseFeeCalculationClass: ItemName.of([Locale.ja, '再リース料計算区分']),
  manualInputReLeaseFee: ItemName.of([Locale.ja, '再リース料(手入力)']),
  reLeasePaymentCycle: ItemName.of([Locale.ja, '再リース支払サイクル']),
  reLeasePaymentDay: ItemName.of([Locale.ja, '再リース支払日']),
  contractDateCondition: ItemName.of([Locale.ja, '契約年月日']),
  hasTotalByContractNoUnits: ItemName.of(
    [Locale.ja, '契約番号単位で集計する'],
    [Locale.en, 'Total By ContractNo Units'],
  ),
  onBalance: ItemName.of([Locale.ja, 'オンバランス'], [Locale.en, 'On Balance']),
  laStartScheduledDate: ItemName.of([Locale.ja, 'リース開始予定年月日']),
  contractSpareNameOneCondition: ItemName.of([Locale.ja, '契約予備名称1']),
  contractSpareNameTwoCondition: ItemName.of([Locale.ja, '契約予備名称2']),
  estimateCashPurchaseAmountCondition: ItemName.of([Locale.ja, '見積現金購入額']),
  conclusionApprovalNoCondition: ItemName.of([Locale.ja, '締結時稟議決裁番号']),
  laStartScheduledDateCondition: ItemName.of([Locale.ja, 'リース開始予定年月日']),
  updatingContractsCondition: ItemName.of([Locale.ja, '更新中契約']),
  leasePeriodCondition: ItemName.of([Locale.ja, 'リース期間']),
  acquiredMergerCondition: ItemName.of([Locale.ja, '合併受入']),
  acquiredMergerDateCondition: ItemName.of([Locale.ja, '合併受入年月日']),
  migrationDate: ItemName.of([Locale.ja, '移行年月日']),
  expiredDate: ItemName.of([Locale.ja, '満了年月日']),
  contractSearchCondition: ItemName.of(
    [Locale.ja, '契約検索条件'],
    [Locale.en, 'Contract Search Conditions'],
  ),
  automaticReLeaseGrid: ItemName.of([Locale.ja, '自動再リース']),
  principalBalance: ItemName.of([Locale.ja, '元本残高']),
  onBalanceGrid: ItemName.of([Locale.ja, 'オンバランス'], [Locale.en, 'On Balance']),
  nextContractNo: ItemName.of([Locale.ja, '次回の契約番号']),
} satisfies ItemNames<Omit<Schema, 'laContractId'>>;

const labelNames = {
  contractInformation: ItemName.of([Locale.ja, '契約情報']),
  reLeaseContractInformation: ItemName.of([Locale.ja, '再リース契約情報']),
  nextContractInformation: ItemName.of([Locale.ja, '次回契約情報']),
  automaticReLease: ItemName.of([Locale.ja, '自動再リース']),
  automaticReLeaseInformation: ItemName.of([Locale.ja, '自動再リース情報']),
  laStartRegistration: ItemName.of([Locale.ja, 'リース開始登録']),
  contractExpiredNotification: ItemName.of([Locale.ja, 'リース満了日の近い契約の件数:{0}件']),
  referenceLaContractRegistrationHistory: ItemName.of([Locale.ja, 'リース契約登録履歴照会']),
  laStartImportConfirm: ItemName.of(
    [Locale.ja, 'リース開始登録(取込)確認'],
    [Locale.en, 'Lease Start Registration Import Confirm'],
  ),
  reLeaseAndContractExpired: ItemName.of(
    [Locale.ja, '再リース及び契約満了'],
    [Locale.en, 'Re Lease And Contract Expired'],
  ),
  reLeaseAndContractExpiredImport: ItemName.of(
    [Locale.ja, '再リース及び契約満了(取込)'],
    [Locale.en, 'Re Lease And Contract Expired Import'],
  ),
  reLeaseAndContractExpiredImportConfirm: ItemName.of(
    [Locale.ja, '再リース及び契約満了(取込)確認'],
    [Locale.en, 'Re Lease And Contract Expired Import Confirm'],
  ),
  laCancelBefMaturity: ItemName.of([Locale.ja, 'リース中途解約'], [Locale.en, 'La Cancel Bef Maturity']),
  laCancelBefMaturityImport: ItemName.of(
    [Locale.ja, 'リース中途解約(取込)'],
    [Locale.en, 'La Cancel Bef Maturity Import'],
  ),
  laCancelBefMaturityImportConfirm: ItemName.of(
    [Locale.ja, 'リース中途解約(取込)確認'],
    [Locale.en, 'La Cancel Bef Maturity Import Confirm'],
  ),
  laContractInfoChange: ItemName.of(
    [Locale.ja, 'リース契約情報変更'],
    [Locale.en, 'La Contract Info Change'],
  ),
  laContractLedgerDetails: ItemName.of(
    [Locale.ja, 'リース契約台帳(1件別明細)'],
    [Locale.en, 'LaContract Ledger Details'],
  ),
  laCancelRegistration: ItemName.of([Locale.ja, 'リース登録取消'], [Locale.en, 'La Cancel Registration']),
  laStartImport: ItemName.of(
    [Locale.ja, 'リース開始登録(取込)'],
    [Locale.en, 'Lease Start Registration Import'],
  ),
  cancellationInformation: ItemName.of([Locale.ja, '解約情報']),
  acquiredMergerInformation: ItemName.of([Locale.ja, '合併受入情報']),
  expiredDate: ItemName.of([Locale.ja, '満了年月日'], [Locale.en, 'Expired Date']),
  leaseContractListReference: ItemName.of(
    [Locale.ja, 'リース契約一覧照会'],
    [Locale.en, 'Lease Contract List Reference'],
  ),
  doNotAutomaticReLease: ItemName.of(
    [Locale.ja, '自動再リースしない'],
    [Locale.en, 'Do Not Re-lease Automatically'],
  ),
  laContractInfoChangeImport: ItemName.of([Locale.ja, 'リース契約情報変更(取込)']),
  laContractInfoChangeImportConfirm: ItemName.of([Locale.ja, 'リース契約情報変更(取込)確認']),
  reLeaseContractInfo: ItemName.of([Locale.ja, '再リース契約情報']),
  nextContractInfo: ItemName.of([Locale.ja, '次回契約情報']),
  laMigrateBalanceImport: ItemName.of([Locale.ja, 'リース残高移行(取込)']),
  laMigrateBalanceImportConfirm: ItemName.of([Locale.ja, 'リース残高移行(取込)確認']),
};

const linkNames = {
  pageTopLink: ItemName.of([Locale.ja, 'ページの先頭へ']),
  contractInfoLink: ItemName.of([Locale.ja, '契約情報']),
  paymentInfoLink: ItemName.of([Locale.ja, '支払情報']),
  articleInfoLink: ItemName.of([Locale.ja, '物件情報']),
};

const titleNames = {
  contractArticleTitle: ItemName.of([Locale.ja, 'リース開始登録-契約と物件情報入力']),
  autoJudgeTitle: ItemName.of([Locale.ja, 'リース開始登録-自動判定情報入力']),
  laStartConfirmTitle: ItemName.of([Locale.ja, 'リース開始登録-登録内容確認']),
  contractArticleInput: ItemName.of([Locale.ja, '契約と物件情報入力']),
  autoJudgeInput: ItemName.of([Locale.ja, '自動判定情報入力']),
  laStartConfirm: ItemName.of([Locale.ja, '登録内容確認']),
};

const buttonName = {
  inputSupport: ItemName.of([Locale.ja, '入力補助']),
};

const laContract = {
  buttonName,
  labelNames,
  names,
  linkNames,
  titleNames,
  attributes,
};

interface LaContract {
  schema: Schema;
  record: Record;
}

export { laContract };
export type { LaContract };
